/**
 * Группировка правок на экране ревью (§6.3) и заголовок группы.
 *
 * Порядок групп задан спекой и совпадает с порядком в Telegram-сообщении:
 * конфликты (отдельным блоком наверху) → новые бренды → бренды по алфавиту →
 * массовые правки → архивация и отвязки.
 *
 * Два решения внутри:
 *  · бренд, созданный в черновике, сворачивается в одну строку вместе со всеми
 *    своими правками — «Smeg · Розница · тир A+ · категории: …» (§6.3);
 *  · правки с общим `operationId` — одна массовая правка: свёрнутая строка,
 *    которая раскрывается в список брендов, у каждого своё «убрать» (§5.7).
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import type { Change, ChannelCode } from '@shared/types.ts'
import { IconChevronDown, IconChevronRight } from '../../components/index.js'
import { countOf, forms } from '../../texts.js'
import { brandNameOf, ChangeLine, changeTitle, fieldTitle, isValuePair, valueText, type ReviewContext } from './ChangeLine.js'
import { draftTexts } from './texts.draft.js'
import './ChangeGroup.css'

// ───────────────────────────── модель группировки ────────────────────────────

export type ReviewLineModel =
  | { kind: 'change'; id: string; change: Change; dropIds: number[] }
  | {
      kind: 'task-done'
      id: string
      /** Сама галочка: по ней считается конфликт и адрес правки. */
      change: Change
      title: string
      /** Отчёт о выполнении — он же значение строки. */
      report: string | null
      dropIds: number[]
    }
  | {
      kind: 'brand-create'
      id: string
      title: string
      summary: string
      changes: Change[]
      dropIds: number[]
    }
  | { kind: 'operation'; id: string; title: string; changes: Change[]; dropIds: number[] }

export interface ReviewGroupModel {
  id: string
  title: string
  /** Счётчик справа от заголовка: «3 правки». */
  count: number
  /** Счётчик в скобках прямо в заголовке: «🆕 Новые бренды (1)». */
  countInTitle: boolean
  lines: ReviewLineModel[]
}

const collator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true })

/** Служебные правки уходят в конец списка — они не про значения полей. */
function isService(change: Change): boolean {
  return change.action === 'archive' || change.action === 'restore' || change.action === 'detach'
}

/**
 * Закрытие задачи — одно действие, но правок у него две: отчёт о выполнении
 * и галочка. Показывать их порознь нечестно: человек отметил задачу и
 * написал, что сделал, а ревью рассказывало об этом как о двух разных делах
 * (и в чат уходило тем же дублем). Пара собирается в одну строку.
 */
export function taskDonePairs(changes: Change[]): {
  reportOf: Map<number, Change>
  hidden: Set<number>
} {
  const byCommentId = new Map<number, Change>()
  for (const change of changes) {
    if (change.action === 'comment' && change.entityType === 'comment' && change.entityId !== null) {
      byCommentId.set(change.entityId, change)
    }
  }
  const reportOf = new Map<number, Change>()
  const hidden = new Set<number>()
  for (const change of changes) {
    if (change.fieldCode !== 'taskDone') continue
    const reportId = change.key.doneCommentId
    const report = typeof reportId === 'number' ? byCommentId.get(reportId) : undefined
    if (!report) continue
    reportOf.set(change.id, report)
    hidden.add(report.id)
  }
  return { reportOf, hidden }
}

/** Текст задачи из адреса правки: имя бренда отсюда вычитается. */
function taskLabel(change: Change): string {
  const parts = change.entityLabel.split('·').map((part) => part.trim()).filter(Boolean)
  const tail = parts.slice(1).join(' · ')
  return tail || parts[0] || draftTexts.review.taskFallback
}

function taskDoneLine(change: Change, report: Change | undefined): ReviewLineModel {
  const what = taskLabel(change)
  return {
    kind: 'task-done',
    id: `change-${change.id}`,
    change,
    title: change.newValue === true
      ? draftTexts.review.taskDone(what)
      : draftTexts.review.taskReopened(what),
    report: report ? draftTexts.review.taskReport(valueText(report, 'new')) : null,
    dropIds: report ? [change.id, report.id] : [change.id],
  }
}

export function buildReviewGroups(changes: Change[], ctx: ReviewContext): ReviewGroupModel[] {
  const { reportOf, hidden } = taskDonePairs(changes)
  const createdBrands = new Set<number>()
  for (const change of changes) {
    if (change.action === 'create' && change.entityType === 'brand' && change.brandId !== null) {
      createdBrands.add(change.brandId)
    }
  }

  const newBrands = new Map<number, Change[]>()
  const operations = new Map<string, Change[]>()
  const brands = new Map<number, Change[]>()
  const service: Change[] = []
  const other: Change[] = []

  // Первый проход: массовые операции узнаются только по числу правок в них.
  for (const change of changes) {
    if (!change.operationId || hidden.has(change.id)) continue
    if (change.brandId !== null && createdBrands.has(change.brandId)) continue
    push(operations, change.operationId, change)
  }
  for (const [id, list] of [...operations]) if (list.length < 2) operations.delete(id)

  for (const change of changes) {
    if (hidden.has(change.id)) continue
    if (change.brandId !== null && createdBrands.has(change.brandId)) {
      push(newBrands, change.brandId, change)
      continue
    }
    if (change.operationId && operations.has(change.operationId)) continue
    if (isService(change)) { service.push(change); continue }
    if (change.brandId !== null) { push(brands, change.brandId, change); continue }
    other.push(change)
  }

  const groups: ReviewGroupModel[] = []

  if (newBrands.size > 0) {
    const lines = [...newBrands.entries()]
      .map(([brandId, list]) => newBrandLine(brandId, list, ctx))
      .filter((line): line is ReviewLineModel => line !== null)
      .sort((a, b) => collator.compare(a.id, b.id))
    groups.push({
      id: 'new-brands',
      title: draftTexts.review.groupNewBrands,
      count: newBrands.size,
      countInTitle: true,
      lines,
    })
  }

  const brandGroups = [...brands.entries()]
    .map(([brandId, list]) => ({
      brandId,
      name: list[0] ? brandNameOf(list[0], ctx) : String(brandId),
      list,
    }))
    .sort((a, b) => collator.compare(a.name, b.name))

  for (const group of brandGroups) {
    groups.push({
      id: `brand-${group.brandId}`,
      title: group.name,
      count: group.list.length,
      countInTitle: false,
      lines: group.list.map((change) => (
        change.fieldCode === 'taskDone'
          ? taskDoneLine(change, reportOf.get(change.id))
          : { kind: 'change' as const, id: `change-${change.id}`, change, dropIds: [change.id] }
      )),
    })
  }

  for (const [operationId, list] of operations) {
    groups.push({
      id: `operation-${operationId}`,
      title: draftTexts.review.groupOperation,
      count: list.length,
      countInTitle: false,
      lines: [{
        kind: 'operation',
        id: `operation-${operationId}`,
        title: operationTitle(list),
        changes: list,
        dropIds: list.map((change) => change.id),
      }],
    })
  }

  if (service.length > 0) {
    groups.push({
      id: 'service',
      title: draftTexts.review.groupService,
      count: service.length,
      countInTitle: false,
      lines: service.map((change) => ({
        kind: 'change' as const,
        id: `change-${change.id}`,
        change,
        dropIds: [change.id],
      })),
    })
  }

  if (other.length > 0) {
    groups.push({
      id: 'other',
      title: draftTexts.review.groupOther,
      count: other.length,
      countInTitle: false,
      lines: other.map((change) => ({
        kind: 'change' as const,
        id: `change-${change.id}`,
        change,
        dropIds: [change.id],
      })),
    })
  }

  return groups
}

function push<K>(map: Map<K, Change[]>, key: K, change: Change): void {
  const list = map.get(key)
  if (list) list.push(change)
  else map.set(key, [change])
}

/** Новый бренд — одна строка: канал, приоритет, видимость, категории (§6.3). */
function newBrandLine(brandId: number, list: Change[], ctx: ReviewContext): ReviewLineModel | null {
  const create = list.find((change) => change.action === 'create' && change.entityType === 'brand')
  if (!create) return null

  const name = brandNameOf(create, ctx)
  const channels = new Set<ChannelCode>()
  for (const change of list) if (change.key.channelCode) channels.add(change.key.channelCode)
  const channelTitle = [...channels]
    .map((code) => ctx.channelNames.get(code) ?? code)
    .join(' и ')

  const value = (field: string): string | null => {
    const found = list.find((change) => change.fieldCode === field && change.entityType === 'brand_channel')
    return found ? valueText(found, 'new') : null
  }

  const details: string[] = []
  const tier = value('tier')
  if (tier) details.push(`${draftTexts.review.tierPrefix} ${tier}`)
  const visibility = value('visibility')
  if (visibility) details.push(`${draftTexts.review.visibilityPrefix} ${visibility}`)
  const seo = value('seoPriority')
  if (seo) details.push(`${draftTexts.review.seoPrefix} ${seo}`)
  const context = value('contextPriority')
  if (context) details.push(`${draftTexts.review.contextPrefix} ${context}`)
  const url = value('url')
  if (url) details.push(`${draftTexts.review.urlPrefix} ${url}`)

  const categories = list
    .filter((change) => change.entityType === 'brand_category'
      && (change.action === 'create' || change.action === 'attach'))
    .map((change) => {
      const label = change.entityLabel.split('·').map((part) => part.trim()).filter(Boolean)
      const categoryName = label.length > 1 ? label[label.length - 1] : null
      if (!categoryName) return null
      const tierChange = list.find((item) => item.entityType === 'brand_category_channel'
        && item.key.brandCategoryId === change.entityId && item.fieldCode === 'tier')
      return tierChange ? `${categoryName} (${valueText(tierChange, 'new')})` : categoryName
    })
    .filter((item): item is string => item !== null)

  if (categories.length > 0) {
    details.push(`${draftTexts.review.categoriesPrefix}: ${categories.join(', ')}`)
  }

  return {
    kind: 'brand-create',
    id: name,
    title: channelTitle ? `${name} · ${channelTitle}` : name,
    summary: details.length > 0 ? details.join(' · ') : draftTexts.review.brandAdded,
    changes: list,
    dropIds: [create.id],
  }
}

/** «Приоритет SEO → TOP-3 · 12 брендов» (§6.3). */
function operationTitle(list: Change[]): string {
  const first = list[0]
  if (!first) return draftTexts.review.groupOperation
  const field = fieldTitle(first) ?? draftTexts.review.groupOperation
  const values = new Set(list.map((change) => valueText(change, 'new')))
  const brandIds = new Set(list.map((change) => change.brandId).filter((id): id is number => id !== null))
  const single = [...values][0]
  if (values.size === 1 && single !== undefined && brandIds.size > 0) {
    return `${field} → ${single} · ${countOf(brandIds.size, forms.brand)}`
  }
  return `${field} · ${countOf(list.length, forms.change)}`
}

// ───────────────────────────── заголовок группы ──────────────────────────────

export interface ChangeGroupProps {
  title: string
  /** Счётчик правок справа от заголовка. */
  count?: number | null
  /** Счётчик в скобках внутри заголовка — для «Новые бренды (1)». */
  countInTitle?: boolean
  children: ReactNode
}

export function ChangeGroup({ title, count = null, countInTitle = false, children }: ChangeGroupProps) {
  const heading = countInTitle && count !== null ? `${title} (${count})` : title
  return (
    <section className="chgroup" aria-label={heading}>
      <header className="chgroup__head">
        <h2 className="chgroup__title t-cell">{heading}</h2>
        {!countInTitle && count !== null ? (
          <span className="chgroup__count t-meta">{countOf(count, forms.change)}</span>
        ) : null}
      </header>
      <ul className="chgroup__lines">{children}</ul>
    </section>
  )
}

// ───────────────────────────── массовая правка ───────────────────────────────

export interface OperationLineProps {
  title: string
  changes: Change[]
  ctx: ReviewContext
  /** Убрать всю операцию целиком. */
  onRemoveAll: () => void
  /** Убрать одну правку из операции. */
  onRemoveOne: (change: Change) => void
}

export function OperationLine({ title, changes, ctx, onRemoveAll, onRemoveOne }: OperationLineProps) {
  const [open, setOpen] = useState(false)

  return (
    <ChangeLine
      title={title}
      onRemove={onRemoveAll}
      removeLabel={draftTexts.review.removeAria(title)}
      leading={(
        <button
          type="button"
          className="chgroup__toggle"
          aria-expanded={open}
          aria-label={open ? draftTexts.review.operationCollapse : draftTexts.review.operationExpand}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <IconChevronDown /> : <IconChevronRight />}
        </button>
      )}
    >
      {open ? (
        <ul className="chgroup__nested">
          {changes.map((change) => (
            <ChangeLine
              key={change.id}
              nested
              title={brandNameOf(change, ctx)}
              from={isValuePair(change) ? valueText(change, 'old') : null}
              to={valueText(change, 'new')}
              onRemove={() => onRemoveOne(change)}
              removeLabel={draftTexts.review.removeAria(`${brandNameOf(change, ctx)} · ${changeTitle(change, ctx)}`)}
            />
          ))}
        </ul>
      ) : null}
    </ChangeLine>
  )
}
